<template>
    <div class="congress-schedule-event-card px-5">
        <div class="congress-schedule-event-card-header" @click="toggleExpanded">
            <div class="congress-schedule-event-card-header-inner">
                <div class="congress-schedule-event-card-header-inner-left">
                    <icon
                        icon="hero/solid/ellipsis-vertical"
                        class="size-4 text-gray-400 dark:text-gray-500 cursor-grab"
                    ></icon>
                </div>
                <div class="congress-schedule-event-card-header-label">
                    <span class="font-medium">
                        {{ room.name || lang('congresses::congresses.accommodations.rooms.untitled') }}
                    </span>
                    <span v-if="room.price" class="text-sm text-gray-500 dark:text-gray-400 ml-2">
                        {{ room.price }} {{ room.currency }}
                    </span>
                </div>
                <div class="congress-schedule-event-card-header-actions">
                    <Button type="button" size="sm" color="danger" variant="inline" @click.stop="$emit('remove')">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/trash"></Icon>
                        </span>
                    </Button>
                    <icon
                        icon="hero/outline/chevron-down"
                        class="congress-schedule-event-card-header-actions-icon"
                        :class="{ 'rotate-180': expanded }"
                    ></icon>
                </div>
            </div>
        </div>

        <div v-show="expanded" class="event-content space-y-2 px-5 mt-2">
            <Field
                v-model="room.name"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.accommodations.rooms.name'),
                    slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.name`,
                    type: 'text',
                    adminComponent: 'AdminText',
                    required: true,
                }"
            />

            <Field
                v-model="room.description"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.accommodations.rooms.description'),
                    slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.description`,
                    type: 'wysiwyg',
                    adminComponent: 'AdminWYSIWYG',
                    options: {
                        text: ['normal', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                        fonts: true,
                        font_sizes: true,
                    },
                }"
            />

            <div class="grid grid-cols-3 gap-2">
                <Field
                    v-model="room.price"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.rooms.price'),
                        slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.price`,
                        type: 'text',
                        adminComponent: 'AdminText',
                        required: true,
                    }"
                />
                <Field
                    v-model="room.capacity"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.rooms.capacity'),
                        slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.capacity`,
                        type: 'number',
                        adminComponent: 'AdminNumber',
                        placeholder: lang('congresses::congresses.accommodations.rooms.capacity_unlimited'),
                    }"
                />
                <Field
                    v-model="room.image_media_id"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.rooms.image'),
                        slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.image_media_id`,
                        type: 'file',
                        adminComponent: 'AdminFile',
                        accept: 'image/*',
                    }"
                />
            </div>

            <!-- Allow Roommates -->
            <Field
                v-model="room.allow_roommates"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.accommodations.rooms.allow_roommates'),
                    slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.allow_roommates`,
                    type: 'checkbox',
                    adminComponent: 'AdminCheckbox',
                    help: lang('congresses::congresses.accommodations.rooms.allow_roommates_help'),
                }"
            />

            <!-- Ticket Restrictions -->
            <Field
                v-if="ticketOptions.length > 0"
                v-model="showTicketRestrictions"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.accommodations.rooms.has_ticket_restrictions'),
                    slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.has_ticket_restrictions`,
                    type: 'checkbox',
                    adminComponent: 'AdminCheckbox',
                }"
            />

            <Field
                v-if="ticketOptions.length > 0 && showTicketRestrictions"
                v-model="room.ticket_ids"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.accommodations.rooms.ticket_restrictions'),
                    slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.ticket_ids`,
                    type: 'select',
                    adminComponent: 'AdminSelect',
                    portal: true,
                    multiple: true,
                    options: ticketOptions,
                    placeholder: lang('congresses::congresses.accommodations.rooms.no_ticket_restriction'),
                    help: lang('congresses::congresses.accommodations.rooms.ticket_restrictions_help'),
                }"
            >
                <template #help>
                    <small>
                        {{ lang('congresses::congresses.accommodations.rooms.ticket_restrictions_help') }}
                    </small>
                    <small v-if="accommodationTicketNames" class="block mt-1 text-blue-600 dark:text-blue-400">
                        {{ lang('congresses::congresses.accommodations.rooms.hotel_ticket_restrictions') }}: {{ accommodationTicketNames }}
                    </small>
                </template>
            </Field>

            <!-- Forbidden Ticket Restrictions -->
            <Field
                v-if="ticketOptions.length > 0"
                v-model="showForbiddenTicketRestrictions"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.accommodations.rooms.has_forbidden_ticket_restrictions'),
                    slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.has_forbidden_ticket_restrictions`,
                    type: 'checkbox',
                    adminComponent: 'AdminCheckbox',
                }"
            />

            <Field
                v-if="ticketOptions.length > 0 && showForbiddenTicketRestrictions"
                v-model="room.forbidden_ticket_ids"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.accommodations.rooms.forbidden_ticket_restrictions'),
                    slug: `accommodations.${props.accommodationIndex}.rooms.${props.roomIndex}.forbidden_ticket_ids`,
                    type: 'select',
                    adminComponent: 'AdminSelect',
                    portal: true,
                    multiple: true,
                    options: ticketOptions,
                    placeholder: lang('congresses::congresses.accommodations.rooms.no_forbidden_ticket_restriction'),
                }"
            >
                <template #help>
                    <small>
                        {{ lang('congresses::congresses.accommodations.rooms.forbidden_ticket_restrictions_help') }}
                    </small>
                </template>
            </Field>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { lang } from '@/lang/composable';
import Button from '@src/Common/Components/Button.vue';
import Field from '@src/Common/Components/Field.vue';
import type { AccommodationRoom } from '../types';

interface Ticket {
    id: number;
    name: string;
}

const props = defineProps<{
    accommodationId: string;
    accommodationIndex: number;
    roomIndex: number;
    availableTickets?: Ticket[];
    accommodationTicketIds?: Ticket[];
}>();

const accommodationTicketNames = computed(() =>
    (props.accommodationTicketIds || []).map((t) => t.name).join(', '),
);

const ticketOptions = computed(() =>
    (props.availableTickets || []).map((t) => ({
        id: t.id,
        name: t.name,
    })),
);

defineEmits<{
    (e: 'remove'): void;
}>();

const room = defineModel<AccommodationRoom>();

const showTicketRestrictions = ref((room.value?.ticket_ids?.length ?? 0) > 0);
const showForbiddenTicketRestrictions = ref((room.value?.forbidden_ticket_ids?.length ?? 0) > 0);

watch(showTicketRestrictions, (val) => {
    if (!val && room.value) {
        room.value.ticket_ids = [];
    }
});

watch(showForbiddenTicketRestrictions, (val) => {
    if (!val && room.value) {
        room.value.forbidden_ticket_ids = [];
    }
});

const expanded = ref(false);

const toggleExpanded = () => {
    expanded.value = !expanded.value;
};
</script>
